"use client";

import { BugIcon } from "@phosphor-icons/react";
import { useState } from "react";
import CommonDialog from "@/components/common/common-dialog/common-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/i18n/client";
import { discordRepository } from "@/repositories/integrations/discord-repository";
import { buildErrorContext } from "@/utils/error-context";

type DiscordReportButtonProps = {
  triggerText?: string;
  title?: string;
  description?: string;
  submitText?: string;
  cancelText?: string;
  error?: Error;
  extraContext?: Record<string, unknown>;
  variant?: "default" | "secondary" | "destructive" | "outline" | "ghost" | "link";
};

export default function DiscordReportButton(props: DiscordReportButtonProps) {
  const { t } = useTranslation("discord");
  const {
    triggerText = t("triggerText"),
    title = t("title"),
    description = t("description"),
    submitText = t("submit"),
    cancelText = t("cancel"),
    error,
    extraContext,
    variant,
  } = props;

  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit() {
    setLoading(true);
    try {
      const context = buildErrorContext({ error, extra: extraContext });
      await discordRepository.sendReport({ message, context });
      toast({ description: t("successToast"), variant: "success" });
      setOpen(false);
      setMessage("");
    } catch (submitError) {
      console.error("Failed to send Discord error report:", submitError);
      toast({
        description: t("errorToast"),
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <CommonDialog
      icon={<BugIcon className="h-5 w-5" />}
      trigger={
        <Button variant={variant} className="w-full" size="lg">
          <BugIcon className="h-5 w-5" />
          {triggerText}
        </Button>
      }
      title={title}
      description={description}
      primaryButtonText={submitText}
      secondaryButtonText={cancelText}
      onPrimaryButtonClick={handleSubmit}
      onSecondaryButtonClick={() => setOpen(false)}
      open={open}
      onOpenChange={setOpen}
      loading={loading}
    >
      <div className="flex flex-col gap-3">
        <Textarea
          placeholder={t("description")}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="min-h-36"
        />
      </div>
    </CommonDialog>
  );
}
